import { Modal, message } from 'antd';
import styled from '@emotion/styled';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { usePresetTimeMutate } from '@knowledge/knowledges/general/edit/atom/presetTime';
import { useTranslate } from '@tolgee/react';
import { useMemo } from 'react';
import {
  weekText,
  dayText,
  foreverText,
} from '@knowledge/knowledges/general/KnowledgeListForm/ConditionRenderer';
import { LoopTag, FixedTag, ExtraTag } from './Tag';
import checksvg from '../../../icon/gouxuan.svg';

type TimeCardProps = {
  open: () => void;
  data: Knowledge.PresetTime;
  ineffective: boolean;
  selected: boolean;
  onClick: () => void;
  noHoverStyle?: boolean;
};

function TimeCard(props: TimeCardProps) {
  const { open, data, selected, onClick, noHoverStyle = false } = props;
  const { t } = useTranslate();
  const { deletePT } = usePresetTimeMutate();
  const { name, dtScope } = data;
  const deleteIt = (id) => {
    Modal.confirm({
      title: t('knowledge.delete.presetTime.title'),
      content: t('knowledge.delete.presetTime.content'),
      okText: t('phrase.确定'),
      cancelText: t('phrase.取消'),
      onOk: async () => {
        try {
          await deletePT(id);
          message.success(t('phrase.删除成功'));
        } catch (e) {
          message.error(t('phrase.删除失败，请稍后再试'));
        }
      },
    });
  };

  const class1 =
    'text-[13px] text-[#515359] bg-[#f7f9fa] flex items-center h-[32px] px-[8px] basis-[100%] flex-shrink-0';
  const class2 = `${class1} basis-[130px]`;

  const ranges = useMemo(() => {
    if (dtScope.filterCode === 'DT_SPECIFIED') {
      return dtScope.specifiedDtRange?.map((each) => {
        const { startAt, endAt } = each;
        return (
          <div className={class1}>
            <div>{startAt}</div>
            &nbsp;-&nbsp;
            <div>{foreverText(t, endAt)}</div>
          </div>
        );
      });
    }
    if (dtScope.filterCode === 'DT_REPEAT') {
      if (dtScope.repeatType === 'YEAR') {
        return dtScope.yearRepeat?.[0]?.dtRanges?.map((each) => {
          const { startAt, endAt } = each;
          return (
            <div className={class1}>
              <div>{startAt}</div>
              &nbsp;-&nbsp;
              <div>{foreverText(t, endAt)}</div>
            </div>
          );
        });
      }
      if (dtScope.repeatType === 'WEEK') {
        return dtScope.weekRepeat?.[0]?.timeRanges?.map((each) => {
          const { startAt, endAt } = each;
          return (
            <div className={class2}>
              <div>{startAt}</div>
              &nbsp;-&nbsp;
              <div>{foreverText(t, endAt)}</div>
            </div>
          );
        });
      }
      if (dtScope.repeatType === 'DAY') {
        return dtScope.dayRepeat?.[0]?.timeRanges?.map((each) => {
          const { startAt, endAt } = each;
          return (
            <div className={class2}>
              <div>{startAt}</div>
              &nbsp;-&nbsp;
              <div>{foreverText(t, endAt)}</div>
            </div>
          );
        });
      }
    }
    return null;
  }, [dtScope, class1, class2, t]);

  const extraTagContent = useMemo(() => {
    if (dtScope.repeatType === 'YEAR') {
      return t('multiTimeSelect.每年');
    }
    if (dtScope.repeatType === 'WEEK') {
      return weekText(t, dtScope);
    }
    if (dtScope.repeatType === 'DAY') {
      return dayText(t, dtScope);
    }
  }, [t, dtScope]);

  return (
    <Container selected={selected} onClick={onClick} hoverStyle={!noHoverStyle}>
      <Header>
        <Title title={name}>{name}</Title>
        <Ops>
          <DeleteOutlined
            onClick={(e) => {
              e.stopPropagation();
              deleteIt(data.id);
            }}
            style={{ color: '#84868C', marginRight: '15px' }}
          />
          <EditOutlined
            onClick={(e) => {
              e.stopPropagation();
              open(data);
            }}
            style={{ color: '#84868C' }}
          />
        </Ops>
      </Header>
      <Content>
        <Range>{ranges}</Range>

        <Tags>
          {dtScope.filterCode === 'DT_REPEAT' ? (
            <LoopTag>{t('knowledge.循环')}</LoopTag>
          ) : (
            <FixedTag>{t('knowledge.固定')}</FixedTag>
          )}

          {extraTagContent && <ExtraTag title={extraTagContent}>{extraTagContent}</ExtraTag>}
        </Tags>
      </Content>
    </Container>
  );
}

const Container = styled.div<{ selected: boolean; hoverStyle: boolean }>`
  background: #fff;
  border: 1px solid rgba(240,242,245,1);
  border-radius: 2px;
  width: 292px;
  height: 151px;
  padding: 12px;
  ${(props) =>
    props.selected &&
    `
    border-color: rgba(51,111,255,1);
    background-image: url("${checksvg}");
    background-repeat: no-repeat;
    background-position: right bottom;
  `}
  &:hover {
    ${(props) =>
      props.hoverStyle &&
      `
      border-color: rgba(51,111,255,1);
    `}
`;

const Header = styled.div`
  height: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Title = styled.div`
  color: #515359;
  font-size: 14px;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`;

const Content = styled.div``;

const Ops = styled.div`
  cursor: pointer;
  flex-shrink: 0;
`;

const Range = styled.div`
  margin-top: 7px;
  height: 80px;
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
`;

const Tags = styled.div`
  overflow: hidden;
  display: flex;
  & > div {
    &:nth-child(2) {
      margin-left: 6px;
    }
  }
`;

export default TimeCard;
